<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Jobs programados</h2>
        <p class="text-muted mb-0">
          Backfill nocturno de imágenes y resumen diario de ventas/caja por email.
        </p>
      </div>
      <router-link class="btn btn-outline-secondary btn-sm" :to="{ name: 'MaintenanceHome' }">
        Volver a Mantenimiento
      </router-link>
    </header>

    <div v-if="!canManageJobs" class="alert alert-warning mb-0">
      Esta sección está disponible sólo para usuarios con el permiso <code>gestionar_jobs</code>.
    </div>

    <template v-else>
      <div v-if="loadError" class="alert alert-danger py-2">{{ loadError }}</div>

      <div v-for="job in jobs" :key="job.clave" class="card shadow-sm">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">
            <div>
              <h3 class="h6 mb-1">{{ job.nombre }}</h3>
              <span class="text-muted small">{{ job.clave }}</span>
            </div>
            <span class="badge" :class="estadoBadgeClass(job.ultimo_estado)">
              {{ estadoLabel(job.ultimo_estado) }}
            </span>
          </div>

          <div class="row g-3 align-items-end mt-2">
            <div class="col-auto form-check form-switch">
              <input
                :id="`activo-${job.clave}`"
                class="form-check-input"
                type="checkbox"
                :checked="job.activo"
                :disabled="savingClave === job.clave"
                @change="toggleActivo(job)"
              />
              <label class="form-check-label" :for="`activo-${job.clave}`">Activo</label>
            </div>

            <div v-if="job.tipo_programacion === 'diario_hora'" class="col-auto">
              <label class="form-label small mb-1">Hora de ejecución</label>
              <div class="d-flex gap-2">
                <input
                  v-model="horaEdit[job.clave]"
                  type="time"
                  class="form-control form-control-sm"
                  style="width: 130px"
                  :disabled="savingClave === job.clave"
                />
                <button
                  class="btn btn-outline-secondary btn-sm"
                  :disabled="savingClave === job.clave || horaEdit[job.clave] === job.hora_ejecucion?.slice(0, 5)"
                  @click="guardarHora(job)"
                >
                  Guardar
                </button>
              </div>
            </div>

            <div v-else class="col-auto">
              <label class="form-label small mb-1">Intervalo (minutos)</label>
              <span class="d-block">{{ job.intervalo_minutos }}</span>
            </div>

            <div class="col-auto ms-auto">
              <button
                class="btn btn-dark btn-sm"
                :disabled="job.ultimo_estado === 'running' || runningClave === job.clave"
                @click="ejecutarAhora(job)"
              >
                <span v-if="runningClave === job.clave" class="spinner-border spinner-border-sm me-2"></span>
                Ejecutar ahora
              </button>
            </div>
          </div>

          <p class="text-muted small mt-3 mb-0">
            Última corrida: {{ formatoFecha(job.ultimo_inicio_en) }} — Fin: {{ formatoFecha(job.ultimo_fin_en) }}
          </p>
          <p v-if="job.ultimo_error" class="text-danger small mb-0">{{ job.ultimo_error }}</p>
          <p v-if="runError[job.clave]" class="text-danger small mb-0">{{ runError[job.clave] }}</p>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { hasPermission } from '@/services/authSessionService'
import jobsService from '@/services/jobsService'

const canManageJobs = computed(() => hasPermission('gestionar_jobs'))

const jobs = ref([])
const loadError = ref('')
const savingClave = ref(null)
const runningClave = ref(null)
const runError = reactive({})
const horaEdit = reactive({})

let pollingTimer = null

function syncHoraEdit() {
  jobs.value.forEach((job) => {
    if (job.tipo_programacion === 'diario_hora') {
      horaEdit[job.clave] = (job.hora_ejecucion || '').slice(0, 5)
    }
  })
}

function hayJobCorriendo() {
  return jobs.value.some((job) => job.ultimo_estado === 'running')
}

async function fetchJobs() {
  try {
    jobs.value = await jobsService.getJobs()
    loadError.value = ''
    syncHoraEdit()
    if (hayJobCorriendo()) {
      schedulePoll()
    } else {
      stopPolling()
    }
  } catch (e) {
    loadError.value = 'No se pudo cargar la lista de jobs'
  }
}

function schedulePoll() {
  stopPolling()
  pollingTimer = setTimeout(fetchJobs, 2500)
}

function stopPolling() {
  if (pollingTimer) {
    clearTimeout(pollingTimer)
    pollingTimer = null
  }
}

async function toggleActivo(job) {
  savingClave.value = job.clave
  try {
    await jobsService.updateJob(job.clave, { activo: !job.activo })
    await fetchJobs()
  } catch (e) {
    loadError.value = `No se pudo actualizar "${job.nombre}"`
  } finally {
    savingClave.value = null
  }
}

async function guardarHora(job) {
  savingClave.value = job.clave
  try {
    await jobsService.updateJob(job.clave, { hora_ejecucion: `${horaEdit[job.clave]}:00` })
    await fetchJobs()
  } catch (e) {
    loadError.value = `No se pudo actualizar el horario de "${job.nombre}"`
  } finally {
    savingClave.value = null
  }
}

async function ejecutarAhora(job) {
  runningClave.value = job.clave
  runError[job.clave] = ''
  try {
    await jobsService.runJob(job.clave)
    await fetchJobs()
  } catch (e) {
    runError[job.clave] = e?.response?.status === 409
      ? 'Ya había una ejecución en curso.'
      : 'No se pudo ejecutar el job.'
  } finally {
    runningClave.value = null
  }
}

function estadoLabel(estado) {
  return { ok: 'OK', error: 'Error', running: 'En curso' }[estado] || 'Sin corridas'
}

function estadoBadgeClass(estado) {
  return {
    ok: 'text-bg-success',
    error: 'text-bg-danger',
    running: 'text-bg-warning'
  }[estado] || 'text-bg-secondary'
}

function formatoFecha(valor) {
  if (!valor) return '—'
  return new Date(valor).toLocaleString('es-PY')
}

onMounted(() => {
  if (!canManageJobs.value) return
  fetchJobs()
})

onBeforeUnmount(() => {
  stopPolling()
})
</script>
